"use client";

import Image from "next/image";
import { useTranslations } from "next-intl";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";

const IMAGE_BASE = "/image/new-version/fabrication/laser-cutting/machine-shop";
const MACHINE_SHOP_IMAGES = [1, 2, 3, 4, 5].map(
	(n) => `${IMAGE_BASE}/js-precision-laser-cutting-machine-shop-${n}.webp`,
);

const GlobeIcon = ({ className }: { className?: string }) => (
	<svg
		className={`h-14 w-14 sm:h-16 sm:w-16 md:h-[72px] md:w-[72px] ${className ?? "text-themePrimary400"}`}
		viewBox="0 0 48 48"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		<circle cx="24" cy="24" r="20" />
		<ellipse cx="24" cy="24" rx="20" ry="8" />
		<ellipse cx="24" cy="24" rx="8" ry="20" />
	</svg>
);

export default function LaserCuttingMachineShop() {
	const t = useTranslations("laserCuttingMachineShopSection");

	return (
		<section className="py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				<div className="relative mb-8 flex min-h-[72px] items-center md:mb-10 md:min-h-[84px]">
					<h2 className="text-2xl font-bold leading-tight text-black sm:text-[30px] md:text-4xl">
						<span className="block">{t("titleLine1")}</span>
						<span className="block">{t("titleLine2")}</span>
					</h2>
					<div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
						<GlobeIcon className="text-themePrimary400" />
					</div>
				</div>

				<div className="grid grid-cols-1 items-start gap-8 lg:grid-cols-[1.05fr_1fr] lg:gap-10 xl:gap-14">
					<div className="w-full max-w-[640px]">
						<Swiper
							modules={[Autoplay, Pagination]}
							slidesPerView={1}
							spaceBetween={0}
							autoplay={{ delay: 3500, disableOnInteraction: false }}
							pagination={{
								clickable: true,
								bulletClass: "laser-machine-shop-bullet",
								bulletActiveClass: "laser-machine-shop-bullet-active",
							}}
							className="laser-cutting-machine-shop-swiper overflow-hidden pb-8"
						>
							{MACHINE_SHOP_IMAGES.map((src, idx) => (
								<SwiperSlide key={src}>
									<div className="relative aspect-[4/3] w-full overflow-hidden bg-gray-100">
										<Image
											src={encodeURI(src)}
											alt={t("imageAlt", { n: idx + 1 })}
											fill
											className="object-cover"
											sizes="(max-width: 1024px) 100vw, 50vw"
										/>
									</div>
								</SwiperSlide>
							))}
						</Swiper>
					</div>

					<div className="min-w-0 pt-1">
						<p className="mb-6 text-sm leading-relaxed text-black md:text-base">{t("intro")}</p>

						<ul className="mb-8 space-y-7">
							<li>
								<h3 className="mb-2 text-lg font-bold leading-tight text-black sm:text-xl md:text-2xl">
									{t("feature1Title")}
								</h3>
								<p className="text-sm leading-relaxed text-black md:text-base">{t("feature1Desc")}</p>
							</li>
							<li>
								<h3 className="mb-2 text-lg font-bold leading-tight text-black sm:text-xl md:text-2xl">
									{t("feature2Title")}
								</h3>
								<p className="text-sm leading-relaxed text-black md:text-base">{t("feature2Desc")}</p>
							</li>
						</ul>

						<a
							href="/upload"
							target="_blank"
							rel="noopener noreferrer"
							className="inline-flex w-fit items-center justify-center rounded-md bg-themePrimary400 px-6 py-3 text-base font-semibold text-white transition-opacity hover:opacity-90 md:text-lg"
						>
							{t("buttonText")}
						</a>
					</div>
				</div>
			</div>
			<style jsx global>{`
				.laser-cutting-machine-shop-swiper .swiper-pagination {
					bottom: 0 !important;
				}
				.laser-machine-shop-bullet {
					width: 7px;
					height: 7px;
					background: #8ab85c;
					opacity: 0.55;
					border-radius: 9999px;
					display: inline-block;
					margin: 0 5px !important;
				}
				.laser-machine-shop-bullet-active {
					opacity: 1;
					background: #6fa637;
				}
			`}</style>
		</section>
	);
}
